// biome-ignore-all lint/a11y: TODO - address a11y issues later
import type { ColumnDef } from "@tanstack/react-table";
import { Link } from "react-router";
import { CATEGORY_STYLES } from "@/components/metrics/styles";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import type { Salesperson } from "@/hooks/api/use-salespeople";
import {
  DEFAULT_COLUMN_WIDTH,
  DEFAULT_PRIMARY_COLUMN_WIDTHS,
} from "@/lib/column-defaults";
import {
  METRIC_CONFIG,
  type SalespersonMetric,
} from "@/pages/salespeople/salesperson-explorer/salesperson-metrics-config";
import type { SalespersonMetrics } from "@/types/salesperson-types";
import type { UnifiedTableColumn } from "@/types/table-column";

export interface SalespersonColumnsOptions {
  selectedMetrics?: Array<keyof SalespersonMetrics>;
  onMetricDrilldown?: (
    salespersonId: string,
    metric: SalespersonMetric,
  ) => void;
  filterOptions?: {
    team_name?: string[];
  };
}

/** Salesperson column with metric category information */
export interface SalespersonColumn extends UnifiedTableColumn<Salesperson> {
  category?: string;
  categoryStyle?: string;
}

// Simple render function for list view metrics (no secondary text)
function renderSimpleMetric(
  value: number | null,
  isCurrency?: boolean,
): React.ReactNode {
  if (value === null || value === undefined) {
    return <span className="text-muted-foreground">-</span>;
  }

  const formattedValue = isCurrency
    ? value.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 0,
      })
    : value.toLocaleString("en-US");

  return <span className="font-medium">{formattedValue}</span>;
}

export const getSalespersonColumnDefs = (
  options: SalespersonColumnsOptions = {},
): ColumnDef<Salesperson>[] => {
  const columns = getSalespersonColumns(options);

  // Convert UnifiedTableColumn to TanStack ColumnDef
  return columns.map((col) => ({
    id: col.key.toString(),
    accessorKey: col.key.toString(),
    header: col.title,
    cell: ({ row }) =>
      col.render ? col.render(row.original) : row.getValue(col.key.toString()),
    enableSorting: !!col.sortable,
    enableResizing: true,
    meta: {
      title: col.title,
      sortable: col.sortable,
      filterable: col.filterable,
      filterType: col.filterType,
      filterOptions: col.filterOptions,
      exportable: col.exportable,
      exportValue: col.exportValue,
      exportOnly: col.exportOnly,
      dataType: col.dataType,
      required: col.required,
      categoryStyle: col.categoryStyle,
    },
    size: col.size || DEFAULT_COLUMN_WIDTH,
  }));
};

export const getSalespersonColumns = (
  options: SalespersonColumnsOptions = {},
): SalespersonColumn[] => {
  const { selectedMetrics = [], filterOptions, onMetricDrilldown } = options;

  const columns: SalespersonColumn[] = [
    {
      key: "name",
      title: "Name",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      required: true, // Primary column that cannot be hidden
      size: DEFAULT_PRIMARY_COLUMN_WIDTHS.MEDIUM,
      render: (salesperson: Salesperson) => (
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex items-center gap-2 w-full">
              <Link
                to={`/salespeople/${salesperson.person_id}/explorer`}
                className="text-foreground p-4 rounded hover:bg-accent transition-colors block flex-1"
                title={salesperson.name}
                onClick={(e) => e.stopPropagation()}
              >
                <span className="font-medium">{salesperson.name}</span>
              </Link>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>View in Salesperson Explorer</p>
          </TooltipContent>
        </Tooltip>
      ),
      exportValue: (salesperson: Salesperson) => salesperson.name,
    },
    {
      key: "email",
      title: "Email",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 200,
      render: (salesperson: Salesperson) => salesperson.email || "-",
      exportValue: (salesperson: Salesperson) => salesperson.email || "",
    },
    {
      key: "current_title",
      title: "Title",
      sortable: true,
      filterable: true,
      filterType: "text",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 180,
      render: (salesperson: Salesperson) => salesperson.current_title || "-",
      exportValue: (salesperson: Salesperson) =>
        salesperson.current_title || "",
    },
    {
      key: "team_name",
      title: "Team",
      sortable: true,
      filterable: true,
      filterType: "select",
      defaultSortDirection: "asc",
      nullSort: "last",
      size: 150,
      render: (salesperson: Salesperson) => salesperson.team_name || "-",
      exportValue: (salesperson: Salesperson) => salesperson.team_name || "",
      filterOptions: filterOptions?.team_name || [],
    },
  ];

  // Add metric columns if metrics are selected
  if (selectedMetrics.length > 0) {
    const metricColumns = selectedMetrics
      .map((metricKey) => {
        const metricConfig = METRIC_CONFIG[metricKey as SalespersonMetric];
        if (!metricConfig) return null;

        const categoryStyle = CATEGORY_STYLES[metricConfig.category];

        const column: SalespersonColumn = {
          key: `metric_${metricKey}`,
          title: metricConfig.title,
          columnType: "metric",
          sortable: true,
          filterable: true,
          filterType:
            metricConfig.format === "currency" ? "currency" : "number",
          size: 150,
          defaultSortDirection: "desc",
          nullSort: "last",
          category: metricConfig.category,
          categoryStyle: categoryStyle?.color,
          render: (salesperson: Salesperson) => {
            const metrics = salesperson.metrics;
            const value = metrics?.[metricKey] ?? null;
            const numericValue = typeof value === "number" ? value : null;

            const formattedContent = renderSimpleMetric(
              numericValue,
              metricConfig.format === "currency",
            );

            const clickableContent = onMetricDrilldown ? (
              <button
                className="w-full h-full text-left p-0 hover:underline decoration-2 underline-offset-4 transition-all cursor-pointer"
                onClick={(e) => {
                  e.stopPropagation();
                  onMetricDrilldown(
                    salesperson.person_id,
                    metricKey as SalespersonMetric,
                  );
                }}
              >
                {formattedContent}
              </button>
            ) : (
              formattedContent
            );

            return clickableContent;
          },
          exportValue: (salesperson: Salesperson) => {
            // For CSV export, check if metrics are flattened to top-level properties (SalespersonWithMetrics)
            const flattenedMetricKey = `metric_${metricKey}`;
            const flattenedValue = (
              salesperson as unknown as Record<string, unknown>
            )[flattenedMetricKey];

            if (flattenedValue !== undefined && flattenedValue !== null) {
              return String(flattenedValue);
            }

            // Fallback to nested metrics structure
            if (!salesperson.metrics) {
              return "";
            }

            const metricValue = salesperson.metrics?.[metricKey];
            if (metricValue === undefined || metricValue === null) {
              return "";
            }

            return String(metricValue);
          },
        };

        return column;
      })
      .filter((col): col is SalespersonColumn => col !== null);

    columns.push(...metricColumns);
  }

  return columns;
};
